import { useLayoutEffect, useState } from 'react';

const VIEWPORT_PADDING = 12;
const PANEL_GAP = 6;

/**
 * Calcula posición fija en viewport para el panel (usar con portal en document.body).
 * @param {import('react').RefObject<HTMLElement>} anchorRef
 * @param {import('react').RefObject<HTMLElement>} panelRef
 * @param {boolean} isOpen
 */
export function usePickerPanelPosition(anchorRef, panelRef, isOpen) {
    const [state, setState] = useState({
        placement: 'bottom',
        compact: false,
        position: null,
    });

    useLayoutEffect(() => {
        if (!isOpen) {
            setState({ placement: 'bottom', compact: false, position: null });
            return undefined;
        }

        const update = () => {
            const anchor = anchorRef.current;
            if (!anchor) return;

            const rect = anchor.getBoundingClientRect();
            const panel = panelRef.current;
            const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
            const spaceAbove = rect.top - VIEWPORT_PADDING;
            const naturalHeight = panel?.scrollHeight || 360;

            let placement = 'bottom';
            let available = spaceBelow - PANEL_GAP;

            if (naturalHeight > available && spaceAbove > spaceBelow) {
                placement = 'top';
                available = spaceAbove - PANEL_GAP;
            }

            available = Math.max(180, available);
            const compact = panel ? naturalHeight > available : false;
            const width = Math.min(rect.width, window.innerWidth - VIEWPORT_PADDING * 2);
            const left = Math.max(
                VIEWPORT_PADDING,
                Math.min(rect.left, window.innerWidth - width - VIEWPORT_PADDING),
            );

            if (placement === 'bottom') {
                setState({
                    placement,
                    compact,
                    position: {
                        top: rect.bottom + PANEL_GAP,
                        left,
                        width,
                        maxHeight: available,
                    },
                });
                return;
            }

            setState({
                placement,
                compact,
                position: {
                    bottom: window.innerHeight - rect.top + PANEL_GAP,
                    left,
                    width,
                    maxHeight: available,
                },
            });
        };

        update();
        const frame = requestAnimationFrame(update);
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);

        const observer = panelRef.current
            ? new ResizeObserver(() => requestAnimationFrame(update))
            : null;
        observer?.observe(panelRef.current);

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
            observer?.disconnect();
        };
    }, [anchorRef, panelRef, isOpen]);

    return state;
};
